import React, { useState, useEffect, useRef } from "react";

interface MenuSearchBarProps {
  value: string;
  onChange: (v: string) => void;
  onSubmit?: (q: string) => void;
  /* Suggested queries shown in the dropdown while the input is empty. */
  trending?: string[];
  accent?: string;
  autoFocus?: boolean;
  placeholder?: string;
}

function GlassIcon(props: React.SVGProps<SVGSVGElement>): React.ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      width="22"
      height="22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="16.5" y1="16.5" x2="21" y2="21" />
    </svg>
  );
}

function ClearIcon(props: React.SVGProps<SVGSVGElement>): React.ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

const panelHdrStyle: React.CSSProperties = {
  fontSize: 11.5,
  fontWeight: 700,
  letterSpacing: ".1em",
  textTransform: "uppercase",
  color: "#b0a290",
  padding: "10px 14px 6px",
};

const trendChipStyle: React.CSSProperties = {
  fontFamily: "'Hanken Grotesk', sans-serif",
  fontSize: 13.5,
  fontWeight: 600,
  color: "#5e5246",
  background: "#faf6f0",
  border: "1px solid #ece3d6",
  borderRadius: 999,
  padding: "7px 14px",
  cursor: "pointer",
  transition: "all .15s",
};

export function MenuSearchBar({
  value,
  onChange,
  onSubmit,
  trending = [],
  accent = "#D63E2A",
  autoFocus,
  placeholder = "Search dishes, restaurants, neighborhoods…",
}: MenuSearchBarProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const typing = value.trim().length > 0;

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node))
        setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const focusInput = () => inputRef.current?.focus();

  function submit() {
    setOpen(false);
    onSubmit?.(value);
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
    } else if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  }

  const showPanel = open && !typing && trending.length > 0;

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%" }}>
      <div
        onMouseDown={(e) => {
          const target = e.target as HTMLElement;
          if (target.closest("[data-se-btn]")) return;
          // On the input itself, let the browser handle caret placement and
          // double-click word selection; only redirect clicks on the pill.
          if (target === inputRef.current) return;
          e.preventDefault();
          focusInput();
          setOpen(true);
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          height: 68,
          padding: "0 8px 0 24px",
          borderRadius: 999,
          cursor: "text",
          background: "#fffdfa",
          border: "1px solid " + (open ? accent : "#ece3d6"),
          boxShadow: open
            ? `0 0 0 4px ${accent}26, 0 24px 50px -24px rgba(42,30,24,.42)`
            : "0 18px 40px -26px rgba(42,30,24,.4)",
          transition: "box-shadow .22s ease, border-color .22s ease",
          fontFamily: "'Hanken Grotesk', sans-serif",
        }}
      >
        <span
          style={{
            color: open ? accent : "#9a8c7d",
            display: "flex",
            flexShrink: 0,
            transition: "color .2s",
          }}
        >
          <GlassIcon />
        </span>
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          style={{
            flex: 1,
            minWidth: 0,
            border: "none",
            outline: "none",
            background: "transparent",
            fontFamily: "inherit",
            fontSize: 16,
            fontWeight: 500,
            letterSpacing: "-0.01em",
            color: "#2a1e18",
            padding: 0,
          }}
        />
        {value && (
          <button
            type="button"
            aria-label="Clear"
            onClick={() => {
              onChange("");
              focusInput();
            }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              flexShrink: 0,
              marginRight: 2,
              border: "none",
              borderRadius: 999,
              cursor: "pointer",
              background: "#f1e9dd",
              color: "#8a7d70",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background =
                "#e7dccb";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background =
                "#f1e9dd";
            }}
          >
            <ClearIcon />
          </button>
        )}
        <button
          type="button"
          data-se-btn
          aria-label="Search"
          onClick={submit}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 52,
            height: 52,
            flexShrink: 0,
            border: "2px solid rgba(0,0,0,.14)",
            borderRadius: 999,
            cursor: "pointer",
            background: accent,
            color: "#fff",
            transition: "transform .1s ease, filter .15s ease",
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.filter =
              "brightness(1.06)";
          }}
          onMouseLeave={(e) => {
            const btn = e.currentTarget as HTMLButtonElement;
            btn.style.filter = "none";
            btn.style.transform = "translateY(0)";
          }}
          onMouseDown={(e) => {
            const btn = e.currentTarget as HTMLButtonElement;
            btn.style.transform = "translateY(2px)";
          }}
          onMouseUp={(e) => {
            const btn = e.currentTarget as HTMLButtonElement;
            btn.style.transform = "translateY(0)";
          }}
        >
          <GlassIcon width={22} height={22} />
        </button>
      </div>

      {showPanel && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 12px)",
            left: 0,
            right: 0,
            zIndex: 40,
            background: "#fffdfa",
            border: "1px solid #ece3d6",
            borderRadius: 22,
            boxShadow: "0 30px 70px -30px rgba(42,30,24,.5)",
            padding: 10,
            fontFamily: "'Hanken Grotesk', sans-serif",
            textAlign: "left",
          }}
        >
          <div style={panelHdrStyle}>Trending in San Diego</div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              padding: "4px 10px 12px",
            }}
          >
            {trending.map((t) => (
              <button
                key={t}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(t);
                  setOpen(false);
                  onSubmit?.(t);
                }}
                style={trendChipStyle}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    "#f6efe5";
                  (e.currentTarget as HTMLButtonElement).style.borderColor =
                    "#e0d4c2";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    "#faf6f0";
                  (e.currentTarget as HTMLButtonElement).style.borderColor =
                    "#ece3d6";
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
